/**
 * DocumentsSection — always-private supporting documents for a catalog item.
 *
 * Maps the Updated-upload-object mockup (`Additional Info.html` lines 287–400):
 * - Drag/drop dropzone (PDF, JPG, PNG, TIFF, HEIC; up to 25 MB; up to 50 docs).
 * - Per-row list with file icon, filename, size, upload-date, category select.
 * - Documents are always private — never shown on public detail responses
 *   (enforced by construction in the backend public-detail flow, which does
 *   not query CatalogDocuments).
 * - Download / preview / remove actions.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloudUpload, Delete, Lock, PictureAsPdf, Visibility, Image as ImageIcon } from '@mui/icons-material';
import {
  CatalogDocument,
  DocumentCategory,
  completeDocumentUpload,
  deleteDocument,
  getDocumentDownloadUrl,
  initiateDocumentUpload,
  listDocuments,
} from '../utils/catalogDocumentsApi';
import { logger } from '../utils/logger';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/tiff'];
const MAX_BYTES = 25 * 1024 * 1024;
const MAX_DOCS = 50;

const CATEGORIES: Array<{ value: DocumentCategory; label: string }> = [
  { value: 'appraisal', label: 'Appraisal' },
  { value: 'receipt', label: 'Receipt / Invoice' },
  { value: 'provenance', label: 'Provenance' },
  { value: 'certificate_of_authenticity', label: 'Certificate of Authenticity' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'conservation_report', label: 'Conservation Report' },
  { value: 'exhibition_record', label: 'Exhibition Record' },
  { value: 'auction_record', label: 'Auction Record' },
  { value: 'other', label: 'Other' },
];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface DocumentsSectionProps {
  catalogId: string | null;
  /** Delegator user ID — passed when a delegate is editing the owner's catalog item. */
  ownerId?: string;
  /**
   * When true, renders only the document list with View/Download actions.
   * The drag-and-drop upload zone and delete buttons are hidden.
   * Renders null when there are zero documents after loading.
   * Used on the baseball-card / object-detail page (GH#1216).
   */
  readOnly?: boolean;
}

export const DocumentsSection: React.FC<DocumentsSectionProps> = ({ catalogId, ownerId, readOnly = false }) => {
  const [documents, setDocuments] = useState<CatalogDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    if (!catalogId) {
      setDocuments([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const docs = await listDocuments(catalogId, ownerId);
        if (!cancelled) setDocuments(docs);
      } catch (err) {
        // Initial-load failures suppressed by design (issue #1124):
        // no user action possible on unvisited tab; common cause is no-documents
        // returning non-200 for new catalog items.
        logger.warn('DocumentsSection: failed to load documents (treating as empty)', err);
        if (!cancelled) setDocuments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogId, ownerId]);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!catalogId) return;

      const errors: string[] = [];
      const accepted: File[] = [];
      let remaining = MAX_DOCS - documents.length;
      for (const file of files) {
        if (remaining <= 0) {
          errors.push(`Maximum of ${MAX_DOCS} documents per object reached. ${file.name} skipped.`);
          continue;
        }
        if (!ALLOWED_TYPES.includes(file.type)) {
          errors.push(`${file.name}: unsupported format (${file.type || 'unknown'})`);
          continue;
        }
        if (file.size > MAX_BYTES) {
          errors.push(`${file.name}: exceeds ${formatSize(MAX_BYTES)} limit`);
          continue;
        }
        accepted.push(file);
        remaining -= 1;
      }
      setError(errors.length ? errors.join(' · ') : null);

      for (const file of accepted) {
        try {
          const initiated = await initiateDocumentUpload(catalogId, {
            filename: file.name,
            contentType: file.type,
            sizeBytes: file.size,
            category: 'other',
          }, ownerId);
          const putResp = await fetch(initiated.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          });
          if (!putResp.ok) {
            throw new Error(`S3 PUT failed (${putResp.status})`);
          }
          const completed = await completeDocumentUpload(catalogId, initiated.docId, ownerId);
          setDocuments((prev) => [completed, ...prev]);
        } catch (err) {
          logger.error('DocumentsSection: upload failed', err);
          setError((prev) => [prev, `${file.name}: upload failed`].filter(Boolean).join(' · '));
        }
      }
    },
    [catalogId, ownerId, documents.length]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      void uploadFiles(Array.from(event.target.files));
      event.target.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    if (event.dataTransfer.files.length) {
      void uploadFiles(Array.from(event.dataTransfer.files));
    }
  };

  const handleDownload = async (doc: CatalogDocument) => {
    if (!catalogId) return;
    try {
      const url = await getDocumentDownloadUrl(catalogId, doc.docId, ownerId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      logger.error('DocumentsSection: download failed', err);
      setError(`Failed to download document: ${doc.filename}`);
    }
  };

  const handleDelete = async (doc: CatalogDocument) => {
    if (!catalogId) return;
    try {
      await deleteDocument(catalogId, doc.docId, ownerId);
      setDocuments((prev) => prev.filter((d) => d.docId !== doc.docId));
    } catch (err) {
      logger.error('DocumentsSection: delete failed', err);
      setError('Failed to delete document');
    }
  };

  const handleCategoryChange = (doc: CatalogDocument, event: SelectChangeEvent) => {
    // Category change is a future enhancement — not persisted here yet because
    // the initial endpoint set is upload/list/delete only. Optimistic update so
    // the UI reflects the user's choice, with a TODO for a PATCH endpoint.
    const next = event.target.value as DocumentCategory;
    setDocuments((prev) => prev.map((d) => (d.docId === doc.docId ? { ...d, category: next } : d)));
  };

  // readOnly + no documents → render nothing (don't clutter the card with an empty
  // box). Includes the loading window, so an object with no documents never flashes
  // the "Loading…"/private header before collapsing — the section simply appears
  // once (and only if) documents resolve.
  if (readOnly && documents.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Lock fontSize="small" color="error" />
        <Typography variant="caption" color="text.secondary">
          All documents are private — never shown publicly or in exports.
        </Typography>
      </Box>

      {!readOnly && (
        <Paper
          component="label"
          sx={{
            p: 3,
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : 'divider',
            bgcolor: isDragOver ? 'action.hover' : 'transparent',
            textAlign: 'center',
            cursor: catalogId ? 'pointer' : 'not-allowed',
            opacity: catalogId ? 1 : 0.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (catalogId) setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={catalogId ? handleDrop : undefined}
          data-testid="documents-dropzone"
        >
          <input
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.tif,.tiff"
            onChange={handleFileChange}
            disabled={!catalogId}
            style={{ display: 'none' }}
          />
          <CloudUpload color={isDragOver ? 'primary' : 'action'} />
          <Typography variant="body2">Drag &amp; drop documents here, or click to choose files</Typography>
          <Typography variant="caption" color="text.secondary">
            PDF, JPG, PNG, TIFF, or HEIC · up to {formatSize(MAX_BYTES)} each · {MAX_DOCS} documents max
          </Typography>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <Typography variant="body2" sx={{ mt: 2 }}>Loading…</Typography>}

      {documents.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {documents.map((doc) => (
            <Paper key={doc.docId} sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }} data-testid={`document-row-${doc.docId}`}>
              {doc.contentType === 'application/pdf' ? (
                <PictureAsPdf color="error" />
              ) : (
                <ImageIcon color="action" />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.filename}>
                  {doc.filename}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatSize(doc.sizeBytes)} · {new Date(doc.uploadedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                </Typography>
              </Box>
              {!readOnly && (
                <Select
                  size="small"
                  value={doc.category}
                  onChange={(e) => handleCategoryChange(doc, e)}
                  sx={{ minWidth: 180 }}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
              <Tooltip title="View / Download">
                <IconButton size="small" onClick={() => void handleDownload(doc)} aria-label={`View ${doc.filename}`}>
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
              {!readOnly && (
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => handleDelete(doc)} aria-label={`Remove ${doc.filename}`}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};
